import { realtimeService } from 'src/services/realtime'
import { ActivityTracker } from './activityTracker'
import { PresenceClient } from './client'
import { isSameLocation, locationFromPathname } from './location'
import {
  IPresenceLocation,
  IPresenceTabState,
  TUserStatus,
  isWorseStatus,
} from './types'

export * from './types'
export { locationFromPathname } from './location'

// a navigation burst (redirects, tab switching) collapses into one update
const LOCATION_DEBOUNCE_MS = 2_000
// a tab nobody touched for this long steps out of presence: it frees a slot of the channel
// (Ably caps presence members) and a forgotten tab does not keep the user "away" forever
const LEAVE_AFTER_IDLE_MS = 2 * 60 * 60_000
const IDLE_CHECK_INTERVAL_MS = 60_000
// self-heal: the backend thinks worse of us than this tab knows to be true
const HEAL_AFTER_MS = 15_000
const HEAL_MIN_INTERVAL_MS = 60_000

/**
 * Reports what this tab is doing (active / idle, visible, current page) through Ably presence.
 * The backend turns the tabs of a user into one status; this service never computes it.
 */
class PresenceService {
  readonly activity = new ActivityTracker()
  private client = new PresenceClient()

  private started = false
  private location: IPresenceLocation | null = null
  private locationTimer?: ReturnType<typeof setTimeout>
  private idleTimer?: ReturnType<typeof setInterval>
  private unsubscribeActivity?: () => void

  private worseSince?: number
  private lastHealAt = 0

  /**
   * @param channelName membership channel, as told by the backend (`GET /user` → presence.channel)
   */
  start(channelName: string) {
    if (this.started || !realtimeService.client) return
    this.started = true

    this.location = locationFromPathname(window.location.pathname)

    this.activity.start()
    this.unsubscribeActivity = this.activity.subscribe(() => {
      // any sign of life brings a tab that stepped out back in
      if (this.activity.snapshot().activity === 'active') this.client.resume()
      this.client.update(this.tabState())
    })
    this.idleTimer = setInterval(this.checkLongIdle, IDLE_CHECK_INTERVAL_MS)

    this.client.start(channelName, this.tabState())
  }

  /** Router navigation. Only the pathname is ever reported */
  setPathname(pathname: string) {
    const location = locationFromPathname(pathname)
    if (isSameLocation(location, this.location)) return

    this.location = location
    if (!this.started) return

    if (this.locationTimer) clearTimeout(this.locationTimer)
    this.locationTimer = setTimeout(
      () => this.client.update(this.tabState()),
      LOCATION_DEBOUNCE_MS
    )
  }

  /**
   * The user's own status as the backend sees it. If it stays worse than what this tab knows
   * (we are active, the backend says away / offline) the `enter` was probably lost: sync again.
   */
  reportBackendStatus(status: TUserStatus, isManual: boolean) {
    if (!this.started || isManual || this.client.isPaused()) {
      this.worseSince = undefined

      return
    }

    const expected: TUserStatus =
      this.activity.snapshot().activity === 'active' ? 'online' : 'away'

    if (!isWorseStatus(status, expected)) {
      this.worseSince = undefined

      return
    }

    const now = Date.now()
    this.worseSince = this.worseSince ?? now

    setTimeout(() => {
      if (
        this.worseSince !== undefined &&
        Date.now() - this.worseSince >= HEAL_AFTER_MS &&
        Date.now() - this.lastHealAt >= HEAL_MIN_INTERVAL_MS
      ) {
        this.lastHealAt = Date.now()
        this.worseSince = undefined
        this.client.sync()
      }
    }, HEAL_AFTER_MS)
  }

  /** Logout: leave presence and close the connection before the page goes away */
  async stop() {
    if (this.locationTimer) clearTimeout(this.locationTimer)
    if (this.idleTimer) clearInterval(this.idleTimer)
    this.unsubscribeActivity?.()
    this.activity.stop()
    this.started = false

    await this.client.stop()
    await realtimeService.close()
  }

  private checkLongIdle = () => {
    if (this.activity.idleForMs() > LEAVE_AFTER_IDLE_MS) this.client.pause()
  }

  private tabState(): IPresenceTabState {
    return { ...this.activity.snapshot(), location: this.location }
  }
}

export const presenceService = new PresenceService()
